import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiClient } from "../lib/api";
import ServerPlayerStatsSection from "../components/match/ServerPlayerStatsSection";
import MatchStatusBadge from "../components/MatchStatusBadge";
import { useReferenceLabel } from "../hooks/useReferenceValues";

interface MatchTeamInfo {
  team_id: string;
  name: string;
  score: number;
}

interface MatchData {
  match_id: string;
  home_team: MatchTeamInfo;
  away_team: MatchTeamInfo;
  status: string;
  competition_id: string;
  competition_stage?: string;
  season_name?: string;
  match_date?: string;
  venue?: { name: string };
}

type ActiveTab = "details" | "player-analytics";

export default function MatchDetail() {
  const { matchId } = useParams<{ matchId: string }>();
  const { t } = useTranslation("matches");
  const { t: tLogger } = useTranslation("logger");
  const resolveSeasonLabel = useReferenceLabel("season");
  const [match, setMatch] = useState<MatchData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ActiveTab>("details");

  useEffect(() => {
    if (!matchId) return;
    setLoading(true);
    setError(null);
    apiClient
      .get<MatchData>(`matches/${matchId}`)
      .then((data) => {
        setMatch(data);
      })
      .catch(() => {
        setError(t("error"));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [matchId, t]);

  if (loading) {
    return (
      <div data-testid="match-detail-loading" className="p-8">
        <p className="text-gray-600">{t("loading")}</p>
      </div>
    );
  }

  if (error || !match) {
    return (
      <div data-testid="match-detail-error" className="p-8">
        <p className="text-red-600">{error ?? t("error")}</p>
        <Link
          to="/matches"
          data-testid="match-detail-back"
          className="text-primary-600 hover:underline mt-4 inline-block"
        >
          {t("allMatches")}
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="match-detail-page" className="p-8">
      <div className="mb-4">
        <Link
          to="/matches"
          data-testid="match-detail-back"
          className="text-primary-600 hover:underline text-sm"
        >
          &larr; {t("allMatches")}
        </Link>
      </div>

      <h1
        data-testid="match-detail-title"
        className="text-3xl font-bold text-gray-900 mb-6"
      >
        {t("matchDetails")}
      </h1>

      {/* Tab bar */}
      <div role="tablist" className="flex gap-1 border-b border-gray-200 mb-6">
        <button
          role="tab"
          aria-selected={activeTab === "details"}
          id="tab-details"
          onClick={() => setActiveTab("details")}
          data-testid="match-detail-tab-details"
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "details"
              ? "border-indigo-600 text-indigo-600"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          {t("details", "Details")}
        </button>
        <button
          role="tab"
          aria-selected={activeTab === "player-analytics"}
          id="tab-player-analytics"
          onClick={() => setActiveTab("player-analytics")}
          data-testid="match-detail-tab-player-analytics"
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "player-analytics"
              ? "border-indigo-600 text-indigo-600"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          {tLogger("playerStats.tabLabel", "Player Analytics")}
        </button>
      </div>

      {activeTab === "details" && (
        <div
          role="tabpanel"
          aria-labelledby="tab-details"
          className="card space-y-6"
        >
          {/* Teams and score */}
          <div
            data-testid="match-detail-scoreboard"
            className="flex items-center justify-center gap-8 py-6"
          >
            <div data-testid="match-detail-home-team" className="text-center">
              <p className="text-lg font-semibold text-gray-900">
                {match.home_team.name}
              </p>
              <span className="text-sm text-gray-500">{t("homeTeam")}</span>
            </div>

            <div
              data-testid="match-detail-score"
              className="text-4xl font-bold text-gray-900"
            >
              {match.home_team.score} &ndash; {match.away_team.score}
            </div>

            <div data-testid="match-detail-away-team" className="text-center">
              <p className="text-lg font-semibold text-gray-900">
                {match.away_team.name}
              </p>
              <span className="text-sm text-gray-500">{t("awayTeam")}</span>
            </div>
          </div>

          {/* Metadata */}
          <dl
            data-testid="match-detail-metadata"
            className="grid grid-cols-2 gap-4 text-sm"
          >
            <div>
              <dt className="text-gray-500">{t("status")}</dt>
              <dd data-testid="match-detail-status" className="mt-1">
                <MatchStatusBadge status={match.status} />
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">{t("competition")}</dt>
              <dd
                data-testid="match-detail-competition"
                className="font-medium text-gray-900"
              >
                {match.competition_id}
              </dd>
            </div>
            {match.season_name && (
              <div>
                <dt className="text-gray-500">{t("season")}</dt>
                <dd
                  data-testid="match-detail-season"
                  className="font-medium text-gray-900"
                >
                  {resolveSeasonLabel(match.season_name)}
                </dd>
              </div>
            )}
            {match.venue && (
              <div>
                <dt className="text-gray-500">{t("venue")}</dt>
                <dd
                  data-testid="match-detail-venue"
                  className="font-medium text-gray-900"
                >
                  {match.venue.name}
                </dd>
              </div>
            )}
          </dl>
        </div>
      )}

      {activeTab === "player-analytics" && matchId && (
        <div role="tabpanel" aria-labelledby="tab-player-analytics">
          <ServerPlayerStatsSection matchId={matchId} />
        </div>
      )}
    </div>
  );
}
